import { useEffect, useRef, useState } from 'react';
import { FileText, Lightbulb, Network, Loader2, AlertCircle, ChevronRight, Hash } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { CapsuleBundle } from '../types';
import type { CollectClient } from '../lib/api';
import { ApiError } from '../lib/api';
import { capsuleErrorMessage, isContentUnavailable } from '../lib/capsuleErrors';
import { KGForceGraph } from './KGForceGraph';

type Tab = 'sources' | 'concepts' | 'kg';

export function CapsuleContents({
  client,
  capsuleId,
  onContentUnavailable,
}: {
  client: CollectClient;
  capsuleId: number;
  /** The server answered that it no longer has (or cannot locate) this capsule's content. */
  onContentUnavailable?: () => void;
}) {
  const [bundle, setBundle] = useState<CapsuleBundle | null>(null);
  // The failed read: its code, and this app's own message for it (never the server's text).
  const [error, setError] = useState<{ code: string; message: string; encrypted?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('sources');
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [openConcept, setOpenConcept] = useState<string | null>(null);
  const onUnavailable = useRef(onContentUnavailable);
  onUnavailable.current = onContentUnavailable;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBundle(null);
    client.getCapsulePayload(capsuleId)
      .then((b) => { if (!cancelled) setBundle(b); })
      .catch((e: ApiError) => {
        if (cancelled) return;
        if (isContentUnavailable(e)) onUnavailable.current?.();
        setError({
          code: e?.code || 'ERROR',
          message: capsuleErrorMessage(e),
          // ENCRYPTED is made in the browser (api.ts): its message is our own JSON.
          encrypted: e?.code === 'ENCRYPTED' && e.status === 0 ? e.message : undefined,
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client, capsuleId]);

  if (loading) {
    return (
      <div className="glass-panel rounded-xl p-12 flex items-center gap-3 justify-center" style={{ color: 'rgba(240,253,244,0.55)' }}>
        <Loader2 className="animate-spin" size={18} />
        <span className="text-sm">Decompressing + verifying SHA-256…</span>
      </div>
    );
  }

  if (error) {
    if (error.encrypted !== undefined) {
      // Parse the JSON message we packed in the ApiError
      let info: { alg: string; ek_fp: string; capsuleId: number } | null = null;
      try { info = JSON.parse(error.encrypted); } catch { /* ignore */ }
      const alg = info?.alg || 'ml-kem-768+aes-256-gcm+gzip';
      const fp = info?.ek_fp || '?';
      return (
        <div className="glass-panel-strong rounded-xl p-8" style={{ borderColor: 'rgba(16,185,129,0.4)' }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-none heartbeat" style={{
              background: 'linear-gradient(135deg, #10B981, #064E3B)',
            }}>
              <span style={{ color: '#F0FDF4', fontSize: 22 }}>🔒</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="display text-2xl mb-2">Encrypted capsule</h3>
              <p className="text-sm mb-4" style={{ color: 'rgba(240,253,244,0.7)' }}>
                This capsule is sealed with a real post-quantum envelope. Browser-side decryption
                isn't wired yet — decrypt locally with the CLI using the workspace's private key
                and passphrase.
              </p>
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <div className="glass-panel rounded p-3">
                  <div className="label mb-1">Algorithm</div>
                  <div className="font-mono text-sm" style={{ color: '#34D399' }}>{alg}</div>
                </div>
                <div className="glass-panel rounded p-3">
                  <div className="label mb-1">Public key fingerprint</div>
                  <div className="font-mono text-sm">{fp}</div>
                </div>
              </div>
              <div className="rounded p-4" style={{ background: '#022C22' }}>
                <div className="label mb-2" style={{ color: '#34D399' }}>Decrypt locally</div>
                <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(240,253,244,0.85)' }}>
{`cd Qodoonect-v1/synapse-collect/apps/cli
.venv/Scripts/activate
python collect.py keys decrypt-capsule <workspace> \\
  --capsule-id ${capsuleId} \\
  --output decrypted-${capsuleId}.json`}
                </pre>
              </div>
              <p className="text-xs mt-3" style={{ color: 'rgba(240,253,244,0.5)' }}>
                The CLI fetches the encrypted envelope, runs ML-KEM-768 decapsulation against the workspace's
                <code className="font-mono mx-1" style={{ color: '#34D399' }}>keys/dk.enc</code> (decrypted with your passphrase via PBKDF2 600k iterations),
                AES-256-GCM decrypts the payload, gunzips, and SHA-256 verifies against the capsule's content_hash.
                Browser-side WASM ML-KEM is on the roadmap.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="glass-panel rounded-xl p-6" style={{ borderColor: 'rgba(239,68,68,0.3)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={20} style={{ color: '#F87171' }} className="flex-none mt-0.5" />
          <div className="text-sm" style={{ color: '#FCA5A5' }}>
            <div className="font-semibold mb-1">Cannot open this capsule&apos;s content.</div>
            <div style={{ color: 'rgba(252,165,165,0.85)' }}>{error.message}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!bundle) return null;

  const sourceCount = bundle.sources?.length || 0;
  const conceptCount = bundle.concepts?.length || 0;
  const summaryCount = bundle.summaries?.length || 0;
  const tripleCount = bundle.kg_triples?.length || 0;

  return (
    <div>
      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-4 border-b border-emerald-500/15">
        <TabButton active={tab === 'sources'} onClick={() => setTab('sources')} icon={<FileText size={14} />}>
          Sources <span className="ml-1 opacity-60">{sourceCount}</span>
        </TabButton>
        <TabButton active={tab === 'concepts'} onClick={() => setTab('concepts')} icon={<Lightbulb size={14} />}>
          Concepts <span className="ml-1 opacity-60">{conceptCount}</span>
        </TabButton>
        <TabButton active={tab === 'kg'} onClick={() => setTab('kg')} icon={<Network size={14} />}>
          Knowledge graph <span className="ml-1 opacity-60">{tripleCount}</span>
        </TabButton>
      </div>

      {/* Purpose preview */}
      {bundle.purpose && bundle.purpose.length > 8 && (
        <div className="glass-panel rounded-xl p-4 mb-4" style={{ borderLeft: '3px solid #10B981' }}>
          <div className="label mb-2">Purpose · soul of the wiki</div>
          <pre className="text-sm whitespace-pre-wrap font-sans" style={{ color: 'rgba(240,253,244,0.75)', lineHeight: 1.5 }}>
            {bundle.purpose.length > 320 ? bundle.purpose.slice(0, 320) + '…' : bundle.purpose}
          </pre>
        </div>
      )}

      {/* Tab body */}
      <AnimatePresence mode="wait">
        {tab === 'sources' && (
          <motion.div key="sources" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            {sourceCount === 0 ? (
              <EmptyTab text="No sources in this capsule." />
            ) : (
              <div className="space-y-2">
                {bundle.sources.map((s) => (
                  <FileCard
                    key={s.path}
                    path={s.path}
                    size={s.size}
                    content={s.content}
                    isOpen={openSource === s.path}
                    onToggle={() => setOpenSource(openSource === s.path ? null : s.path)}
                  />
                ))}

                {summaryCount > 0 && (
                  <>
                    <div className="label mt-6 mb-2">Summaries</div>
                    {bundle.summaries.map((s) => (
                      <FileCard
                        key={s.path}
                        path={s.path}
                        size={s.size}
                        content={s.content}
                        isOpen={openSource === s.path}
                        onToggle={() => setOpenSource(openSource === s.path ? null : s.path)}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </motion.div>
        )}

        {tab === 'concepts' && (
          <motion.div key="concepts" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            {conceptCount === 0 ? (
              <EmptyTab text="No concept pages were extracted by the LLM. Likely a small or dense source." />
            ) : (
              <div className="space-y-2">
                {bundle.concepts.map((c) => (
                  <FileCard
                    key={c.path}
                    path={c.path}
                    size={c.size}
                    content={c.content}
                    isOpen={openConcept === c.path}
                    onToggle={() => setOpenConcept(openConcept === c.path ? null : c.path)}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {tab === 'kg' && (
          <motion.div key="kg" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <KGForceGraph triples={bundle.kg_triples || []} height={460} />
            {tripleCount > 0 && (
              <details className="mt-4 glass-panel rounded-xl p-4">
                <summary className="cursor-pointer text-sm flex items-center gap-2" style={{ color: 'rgba(240,253,244,0.6)' }}>
                  <Hash size={14} style={{ color: '#34D399' }} />
                  Browse {tripleCount} triples as a list
                </summary>
                <div className="mt-3 space-y-1 font-mono text-xs">
                  {bundle.kg_triples.map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-1 border-b border-emerald-500/5">
                      <span style={{ color: '#A7F3D0' }} className="truncate text-right">{t.subject}</span>
                      <span style={{ color: 'rgba(240,253,244,0.4)' }}>—{t.predicate}→</span>
                      <span style={{ color: '#34D399' }} className="truncate">{t.object}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors relative"
      style={{
        color: active ? '#10B981' : 'rgba(240,253,244,0.55)',
        borderBottom: active ? '2px solid #10B981' : '2px solid transparent',
        marginBottom: '-1px',
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function FileCard({
  path,
  size,
  content,
  isOpen,
  onToggle,
}: {
  path: string;
  size: number;
  content: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const baseName = path.split('/').pop() || path;
  return (
    <div className="glass-panel rounded-xl">
      <button onClick={onToggle} className="w-full p-4 flex items-center gap-3 text-left">
        <ChevronRight size={16} style={{ color: '#10B981', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0)' }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm font-semibold truncate" style={{ color: '#F0FDF4' }}>{baseName}</div>
          <div className="text-xs" style={{ color: 'rgba(240,253,244,0.4)' }}>{path} · {formatBytes(size)}</div>
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <pre className="text-xs p-4 pt-0 whitespace-pre-wrap font-mono leading-relaxed border-t border-emerald-500/10" style={{ color: 'rgba(240,253,244,0.85)', maxHeight: 480, overflowY: 'auto' }}>
              {content}
            </pre>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyTab({ text }: { text: string }) {
  return (
    <div className="glass-panel rounded-xl p-8 text-center text-sm" style={{ color: 'rgba(240,253,244,0.5)' }}>
      {text}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
